<template>
  <v-dialog
    v-model="displayPromoDialog"
    scrollable
    persistent
    max-width="360"
  >
    <v-card>
      <v-card-title>
        <div class="header pt-3">
          <h4 v-if="name" class="title">
            {{ name }}
          </h4>
        </div>
      </v-card-title>
      <v-card-text class="pb-0">
        <div
          v-if="description"
          v-html="description"
          class="description"
        />
        <img
          v-if="image"
          :src="image"
          alt=""
          class="image"
        />
        <div
          v-if="infoText"
          v-html="infoText"
          class="text-center"
        />
        <v-divider />
        <template v-for="(promoType, index) in promoTypes">
          <div
            :key="index"
            v-if="promoType.type === PROMO_TYPES.text"
            class="promo-text-wrapper mb-4"
          >
            <div class="py-4">
              <span class="promo-codes-label">
                {{`${$tc(
                  'bounties.fetchedPromoBountyCodesLabel',
                  promoCodesLength
                )} ${promoCodesString}`}}
              </span>
              <icon
                v-if="displayCopyFeedback"
                default-icon-name="mdiCheckCircle"
                :size="20"
                role="img"
                color="green"
                class="copy-button"
              />
              <button
                v-else
                v-clipboard:copy="promoCodesString"
                v-clipboard:success="onCopy"
                class="copy-button"
              >
                <icon
                  default-icon-name="mdiContentCopy"
                  :size="20"
                  :label="$t('copy')"
                  role="img"
                />
              </button>
            </div>
            <v-divider />
          </div>
        </template>
        <p
          v-if="expirationDate"
          class="expired-text text-center mb-2"
        >
          {{ $t(
            'bounties.expirationDateText',
            { expirationDate: formattedExpirationDate }
          ) }}
        </p>
        <p
          v-if="isExpired"
          class="expired-text text-center"
        >
          {{ $t('bounties.expiredText') }}
        </p>
      </v-card-text>
      <v-card-actions class="pa-6 flex-column">
        <v-btn
          v-if="promoCodeLink"
          :href="promoCodeLink"
          target="_blank"
          rel="noopener"
          color="primary"
          block
          class="main-button"
        >
          {{ $t('bounties.linkButtonText') }}
        </v-btn>
        <v-btn
          block
          outlined
          class="ma-0"
          @click="showFinishAlertBox"
        >
          {{ $t('bounties.PromoDialogButtonClose') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import Icon from '@/components/ui/Icon'

export default {
  components: {
    Icon,
  },
  name: 'PromoDialog',
  props: {
    fetchedBounty: {
      type: Object,
      required: true,
    },
    showPromoDialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      PROMO_TYPES: {
        text: 'TEXT',
        barcode: 'BARCODE',
        link: 'LINK',
        qrcode: 'QR_CODE'
      },
      displayPromoDialog: this.showPromoDialog,
      displayCopyFeedback: false,
    }
  },
  computed: {
    promoTypes() {
      let types =
        _.get(this.fetchedBounty, 'bounty.promo_redeem_types')

      // If types are not provided, use text type as a default
      if (!_.isArray(types) || _.isEmpty(types)) {
        types = [
          {type: this.PROMO_TYPES.text},
        ]
      }

      return types
    },
    promoCodes() {
      return _.get(this.fetchedBounty, 'fetched_promo_codes', [])
    },
    activatedPromoCodes() {
      return _.get(this.fetchedBounty, 'activated_promo_codes', [])
    },
    promoCodesString() {
      return !_.isEmpty(this.promoCodes)
        ? this.getPromoCodesForFetchedBounty(this.promoCodes)
        : this.getPromoCodesForFetchedBounty(this.activatedPromoCodes)

    },
    promoCodesLength() {
      return Math.max(
        this.promoCodes.length, this.activatedPromoCodes.length
      )
    },
    name() {
      return _.get(this.fetchedBounty, 'bounty.name', '')
    },
    description() {
      return _.get(this.fetchedBounty, 'bounty.description', '')
    },
    image() {
      return _.get(this.fetchedBounty, 'bounty.image', '')
    },
    infoText() {
      return _.get(this.fetchedBounty, 'bounty.promo_info_text', '')
    },
    expirationDate() {
      return _.get(this.fetchedBounty, 'expiration_date')
    },
    formattedExpirationDate() {
      return moment(this.expirationDate).format('DD.MM.YYYY HH:mm:ss')
    },
    // check if bounty is expired (this can only be the case on activated bounties)
    isExpired() {
      let expired = false

      if (
        this.expirationDate &&
        moment(this.expirationDate).isValid() &&
        moment().isAfter(moment(this.expirationDate))
      ) {
        expired = true
      }

      return expired
    },
    promoCodeLink() {
      if (_.isEmpty(this.promoTypes)) {
        return ''
      }

      const foundLink = this.promoTypes.find((promoType) => {
        return promoType.type === this.PROMO_TYPES.link
      })
      let link = _.get(foundLink, 'uri', '')

      if (_.isEmpty(link)) {
        return ''
      }

      // Inject promo code into link placeholder
      link = link.replace(
        '{{promoCode}}',
        decodeURIComponent(this.promoCodesString)
      )

      return link
    }
  },
  methods: {
    getPromoCodesForFetchedBounty(fetchedPromoCodes) {
      const result = []

      for (const promoCode of fetchedPromoCodes) {
        result.push(promoCode.code)
      }

      return result.join(', ')
    },
    onCopy() {
      this.displayCopyFeedback = true

      setTimeout(() => {
        this.displayCopyFeedback = false
      }, 3000)
    },
    showFinishAlertBox() {
      this.displayPromoDialog = false
      this.$emit('finish-pressed')
    }
  }
}
</script>

<style lang="scss" scoped>
.header {
  display: flex;
  flex-direction: column;
  flex-basis: 100%;
  align-items: center;
}

.title {
  letter-spacing: 0;
  text-align: center;
  word-break: normal;
  margin-bottom: 12px;
}

.description {
  line-height: 1.5;
  text-align: center;
  margin-bottom: 16px;

  &::v-deep {
    p {
      line-height: 1.5;
      font-size: 14px;
      margin-bottom: 0;
    }

    li {
      list-style-position: inside;
    }
  }
}

.image {
  display: block;
  margin: 0 auto 16px;
  width: 150px;
  height: 150px;
  object-fit: cover;
}

.copy-button {
  margin-left: 10px;
}

.main-button {
  margin-bottom: 8px;
}
</style>
